/*
 * Profit share, on the Overview. What the partner's clients have invested in
 * total (in crore), the rate that puts the partner on, how far it is to the
 * next rate, the profit Wealthoak makes from those clients and the partner's
 * share of it (GET /partner/profit-share).
 *
 * Display only: nothing here reaches the wallet, and it is labelled apart from
 * the subscription commission so the two aren't confused. The server works out
 * every figure; this only formats them. A backend without the route answers
 * 404, which reads as "not available yet" rather than an error.
 */

import { ApiError, partner, type ProfitShare } from "../api/client";
import { useLoad } from "../api/useLoad";
import { EmptyState, LoadError, SkeletonRows } from "./bits";
import { fmtDateTime, inr, inrCrore, plural, rateLabel } from "./format";
import { BarChart } from "./icons";
import "./profitshare.css";

/** null when the backend has no profit-share route yet, so the card can say so. */
const loadProfitShare = () =>
  partner.profitShare().catch((err): ProfitShare | null => {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  });

export default function ProfitShareCard() {
  const ps = useLoad(loadProfitShare);
  const d = ps.data;

  const body = () => {
    if (ps.error && !d) return <LoadError message={ps.error} onRetry={ps.reload} />;
    if (ps.loading && !d) return <SkeletonRows rows={3} label="Loading profit share" />;
    if (!d) {
      return (
        <EmptyState icon={BarChart} title="Not available yet">
          Profit share isn&apos;t available yet. It will appear here once it is.
        </EmptyState>
      );
    }
    if (d.users === 0) {
      return (
        <EmptyState icon={BarChart} title="No clients yet">
          Profit share starts once a client you referred joins Wealthoak. You&apos;re on the{" "}
          <strong className="num">{rateLabel(d.rate_pct)}</strong> rate until your clients&apos; investment reaches{" "}
          {d.next_tier ? inrCrore(d.next_tier.above_investment) : "the next level"}.
        </EmptyState>
      );
    }
    return <Figures d={d} />;
  };

  return (
    <section className="card card--pad ps" aria-labelledby="ps-title" aria-busy={ps.loading}>
      <div className="card-heading">
        <div>
          <p className="eyebrow">Profit share</p>
          <h2 id="ps-title">
            {d && d.users > 0 ? (
              <>
                <span className="num ps__rate">{rateLabel(d.rate_pct)}</span> of the profit Wealthoak makes from your
                clients
              </>
            ) : (
              "Your share of the profit from your clients"
            )}
          </h2>
          <p className="ps__terms">
            For information only. It isn&apos;t added to your wallet and is separate from your commission.
          </p>
        </div>
      </div>
      {body()}
    </section>
  );
}

function Figures({ d }: { d: ProfitShare }) {
  const next = d.next_tier;
  // Clamped both ways: clients' net cash can be negative, and a negative width
  // is dropped by the browser, which would draw the bar full.
  const progress = next
    ? Math.min(100, Math.max(0, (Number(d.investment) / Number(next.above_investment)) * 100))
    : 100;
  return (
    <>
      <dl className="ps__figures">
        <div className="ps__share">
          <dt>Your share</dt>
          <dd>
            <strong className="num">{inr(d.partner_share)}</strong>
            <small>{rateLabel(d.rate_pct)} of {inr(d.company_profit)}</small>
          </dd>
        </div>
        <div>
          <dt>Wealthoak&apos;s profit from your clients</dt>
          <dd>
            <strong className="num">{inr(d.company_profit)}</strong>
            <small>
              {d.users_sharing} of {plural(d.users, "client")} sharing profit
            </small>
          </dd>
        </div>
        <div>
          <dt>Your clients&apos; investment</dt>
          <dd>
            <strong className="num">{inrCrore(d.investment)}</strong>
            <small>Funds in their broker accounts</small>
          </dd>
        </div>
      </dl>

      <div
        className="ps__bar"
        role="progressbar"
        aria-label="Investment compared with the next rate's threshold"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress)}
      >
        <span style={{ width: `${progress}%` }} />
      </div>
      <p className="ps__next">
        {next ? (
          <>
            <strong className="num">{inrCrore(next.remaining)}</strong> more investment to reach{" "}
            <strong>{rateLabel(next.rate_pct)}</strong>.
          </>
        ) : (
          <>You&apos;re on the top rate.</>
        )}
      </p>
      {d.as_of && <p className="ps__note">Updated {fmtDateTime(d.as_of)}</p>}
    </>
  );
}
